import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import {
  Wallet, CreditCard, Landmark, Save, Loader2, CheckCircle,
  ChevronDown, Shield
} from "lucide-react";
import { supabase } from "../../lib/supabase";

const PAYMENT_METHODS = [
  { id: "bank_card", label: "Банковская карта", icon: CreditCard },
  { id: "bank_account", label: "Банковский счёт (ИП/ООО)", icon: Landmark },
  { id: "yoomoney", label: "ЮMoney", icon: Wallet },
  { id: "sbp", label: "СБП (Система быстрых платежей)", icon: Wallet },
  { id: "tinkoff", label: "Тинькофф", icon: CreditCard },
  { id: "sber", label: "СберБанк", icon: CreditCard }
];

export default function PayoutSettings({ userId }) {
  const [payoutInfo, setPayoutInfo] = useState({
    method: "",
    cardNumber: "",
    cardHolder: "",
    bankName: "",
    bik: "",
    accountNumber: "",
    corrAccount: "",
    inn: "",
    walletId: "",
    phone: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("payout_info")
          .eq("id", userId)
          .single();
        if (!error && data?.payout_info && mounted) {
          setPayoutInfo((prev) => ({ ...prev, ...data.payout_info }));
        }
      } catch (err) {
        console.warn("Failed to load payout info:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [userId]);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          payout_info: payoutInfo,
          updated_at: new Date().toISOString()
        })
        .eq("id", userId);
      if (error) {
        console.error("Failed to save payout info:", error);
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error("Save payout error:", err);
    } finally {
      setSaving(false);
    }
  };

  const selectedMethod = PAYMENT_METHODS.find((m) => m.id === payoutInfo.method);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-primary-400 animate-spin" />
        <span className="ml-3 text-lg font-extralight text-neutral-400">
          Загрузка платёжных данных...
        </span>
      </div>
    );
  }

  return (
    <div id="expert-payout-settings" className="space-y-6">
      <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
        <div className="flex items-center gap-3 mb-2">
          <Wallet className="w-6 h-6 text-primary-400" />
          <h3 className="text-xl font-medium text-white">Платёжные данные</h3>
        </div>
        <p className="text-lg font-extralight text-neutral-400 mb-6">
          Укажите реквизиты, по которым вы будете получать выплаты за консультации
        </p>

        <div className="space-y-5">
          <div>
            <label className="block text-lg font-extralight text-neutral-400 mb-2">
              Способ получения выплат
            </label>
            <div className="relative">
              <select
                value={payoutInfo.method}
                onChange={(e) => setPayoutInfo({ ...payoutInfo, method: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500 appearance-none cursor-pointer"
              >
                <option value="">Выберите способ</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 pointer-events-none" />
            </div>
          </div>

          {payoutInfo.method === "bank_card" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="space-y-4"
            >
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Номер карты
                </label>
                <input
                  type="text"
                  value={payoutInfo.cardNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 16);
                    const formatted = val.replace(/(.{4})/g, "$1 ").trim();
                    setPayoutInfo({ ...payoutInfo, cardNumber: formatted });
                  }}
                  placeholder="0000 0000 0000 0000"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Имя держателя карты
                </label>
                <input
                  type="text"
                  value={payoutInfo.cardHolder}
                  onChange={(e) => setPayoutInfo({ ...payoutInfo, cardHolder: e.target.value })}
                  placeholder="IVAN IVANOV"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500 uppercase"
                />
              </div>
            </motion.div>
          )}

          {(payoutInfo.method === "tinkoff" || payoutInfo.method === "sber") && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="space-y-4"
            >
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Номер карты {payoutInfo.method === "tinkoff" ? "Тинькофф" : "СберБанк"}
                </label>
                <input
                  type="text"
                  value={payoutInfo.cardNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 16);
                    const formatted = val.replace(/(.{4})/g, "$1 ").trim();
                    setPayoutInfo({ ...payoutInfo, cardNumber: formatted });
                  }}
                  placeholder="0000 0000 0000 0000"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Имя держателя
                </label>
                <input
                  type="text"
                  value={payoutInfo.cardHolder}
                  onChange={(e) => setPayoutInfo({ ...payoutInfo, cardHolder: e.target.value })}
                  placeholder="Иван Иванов"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Номер телефона (привязанный к банку)
                </label>
                <input
                  type="tel"
                  value={payoutInfo.phone}
                  onChange={(e) => setPayoutInfo({ ...payoutInfo, phone: e.target.value })}
                  placeholder="+7 (900) 000-00-00"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
            </motion.div>
          )}

          {payoutInfo.method === "bank_account" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="space-y-4"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-lg font-extralight text-neutral-400 mb-2">
                    Наименование банка
                  </label>
                  <input
                    type="text"
                    value={payoutInfo.bankName}
                    onChange={(e) => setPayoutInfo({ ...payoutInfo, bankName: e.target.value })}
                    placeholder="АО Банк"
                    className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-lg font-extralight text-neutral-400 mb-2">
                    БИК
                  </label>
                  <input
                    type="text"
                    value={payoutInfo.bik}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 9);
                      setPayoutInfo({ ...payoutInfo, bik: val });
                    }}
                    placeholder="044525000"
                    maxLength={9}
                    className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Расчётный счёт
                </label>
                <input
                  type="text"
                  value={payoutInfo.accountNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 20);
                    setPayoutInfo({ ...payoutInfo, accountNumber: val });
                  }}
                  placeholder="40802810000000000000"
                  maxLength={20}
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Корреспондентский счёт
                </label>
                <input
                  type="text"
                  value={payoutInfo.corrAccount}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 20);
                    setPayoutInfo({ ...payoutInfo, corrAccount: val });
                  }}
                  placeholder="30101810000000000000"
                  maxLength={20}
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  ИНН
                </label>
                <input
                  type="text"
                  value={payoutInfo.inn}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 12);
                    setPayoutInfo({ ...payoutInfo, inn: val });
                  }}
                  placeholder="000000000000"
                  maxLength={12}
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
            </motion.div>
          )}

          {payoutInfo.method === "yoomoney" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="space-y-4"
            >
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Номер кошелька ЮMoney
                </label>
                <input
                  type="text"
                  value={payoutInfo.walletId}
                  onChange={(e) => setPayoutInfo({ ...payoutInfo, walletId: e.target.value })}
                  placeholder="4100000000000000"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
            </motion.div>
          )}

          {payoutInfo.method === "sbp" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="space-y-4"
            >
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Номер телефона (привязан к СБП)
                </label>
                <input
                  type="tel"
                  value={payoutInfo.phone}
                  onChange={(e) => setPayoutInfo({ ...payoutInfo, phone: e.target.value })}
                  placeholder="+7 (900) 000-00-00"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Банк-получатель
                </label>
                <input
                  type="text"
                  value={payoutInfo.bankName}
                  onChange={(e) => setPayoutInfo({ ...payoutInfo, bankName: e.target.value })}
                  placeholder="Сбербанк, Тинькофф, Альфа-Банк..."
                  className="w-full px-4 py-3 rounded-xl bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                />
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <div className="p-4 rounded-xl bg-primary-950/30 border border-primary-800/30 flex items-start gap-3">
        <Shield className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
        <p className="text-lg font-extralight text-neutral-400">
          Ваши платёжные данные надёжно хранятся и используются исключительно для перевода заработанных средств. Мы не передаём эту информацию третьим лицам.
        </p>
      </div>

      <div className="flex items-center justify-end gap-4">
        {saved && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 text-accent-400"
          >
            <CheckCircle className="w-5 h-5" />
            <span className="text-lg font-extralight">Данные сохранены</span>
          </motion.div>
        )}
        <button
          onClick={handleSave}
          disabled={saving || !payoutInfo.method}
          className="flex items-center gap-2 px-8 py-3 rounded-xl bg-primary-600 hover:bg-primary-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {saving ? "Сохранение..." : "Сохранить реквизиты"}
        </button>
      </div>
    </div>
  );
}
